import { useMemo } from 'react';
import { useWebsiteV2DesignSite } from './useWebsiteV2DesignSite';
import { overlayWebsiteV2Design } from './useWebsiteV2EventDesign.utils';
import type { OverlayableEventDesign } from './useEventWebsiteDesign.types';

export type { OverlayableEventDesign } from './useEventWebsiteDesign.types';

type WebsiteV2EventDesign<T> = Readonly<{
  /** The caller's design, overlaid for a Website V2 event; the same reference when nothing is overlaid. */
  eventDesign: T;
  /** The event's website version or site is still resolving; `eventDesign` is the caller's until it settles. */
  loading: boolean;
}>;

/**
 * For a guest page that themes itself from the event's V1 `EventDesign` (registry, cash funds, …):
 * the same design, with the event's current Website V2 site's design (`useWebsiteVersion`) laid over
 * it, so the page's V1 UI renders in the V2 site's colors, fonts and layout. A V1 event, and a V2 event
 * with no site this viewer may read, get `eventDesign` back untouched.
 *
 * `ssr: false` (the default) because a page without the SSR auth token reads sites anonymously on the
 * server, and the client would keep that anonymous answer from the cache.
 */
export const useWebsiteV2EventDesign = <T extends OverlayableEventDesign | null | undefined>(
  eventDesign: T,
  eventHandle: string,
  { ssr = false }: Readonly<{ ssr?: boolean }> = {},
): WebsiteV2EventDesign<T> => {
  // Read beside the caller's design query rather than after it, so the page waits on neither twice.
  const { site, isLoading } = useWebsiteV2DesignSite(eventHandle, { ssr });
  return useMemo(() => ({ eventDesign: eventDesign && overlayWebsiteV2Design(eventDesign, site), loading: isLoading }), [eventDesign, site, isLoading]);
};
